import { useEffect, useId, useState } from "react";
import { Link2, X } from "lucide-react";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import { cn } from "@/lib/cn";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (url: string) => void;
};

function normalizeLinkUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (/^[\w.-]+\.[a-z]{2,}/i.test(trimmed)) return `https://${trimmed}`;
  return trimmed.includes(".") ? `https://${trimmed}` : null;
}

export function ComposeLinkModal({ open, onClose, onConfirm }: Props) {
  const titleId = useId();
  const [url, setUrl] = useState("https://");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUrl("https://");
    setError(null);
  }, [open]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const normalized = normalizeLinkUrl(url);
    if (!normalized) {
      setError("Indiquez une URL valide (ex. https://exemple.fr).");
      return;
    }
    onConfirm(normalized);
  }

  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      variant="center"
      animation="fade"
      backdrop="transparent"
      panelClassName="max-w-sm"
    >
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="flex items-start gap-2 border-b border-[var(--mail-border-soft)] px-4 py-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--mail-accent)_14%,var(--mail-surface))] text-[var(--mail-accent)]">
            <Link2 className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-sm font-semibold text-[var(--mail-text)]">
              Insérer un lien
            </h2>
            <p className="mt-0.5 text-xs text-[var(--mail-muted)]">
              Sélectionnez du texte puis indiquez l’adresse de destination.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-2 px-4 py-3">
          <label htmlFor={`${titleId}-url`} className="text-[11px] font-semibold uppercase tracking-wide text-[var(--mail-muted)]">
            URL
          </label>
          <input
            id={`${titleId}-url`}
            type="url"
            inputMode="url"
            autoComplete="url"
            autoFocus
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setError(null);
            }}
            placeholder="https://exemple.fr"
            className="mail-form-input w-full rounded-lg px-3 py-2 text-sm"
          />
          {error ? <p className="text-xs text-[var(--mail-danger-text)]">{error}</p> : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--mail-border-soft)] bg-[color-mix(in_srgb,var(--mail-bg)_28%,var(--mail-surface))] px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)]"
          >
            Annuler
          </button>
          <button
            type="submit"
            className={cn(
              "rounded-lg px-3 py-1.5 text-sm font-semibold text-[var(--mail-primary-text)]",
              "mail-btn-primary"
            )}
          >
            Insérer
          </button>
        </div>
      </form>
    </MailModalOverlay>
  );
}
